import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'

import { useAnalytics } from './useAnalytics'

import { selectMembers } from '@/store/features/member/memberSlice'
import { selectCurrentEnterprise, selectEnterpriseLoaded, selectUser } from '@/store/features/user/userSlice'

export const AnalyticsProvider = ({ children }: { children: React.ReactNode }) => {
  const { identify, group } = useAnalytics()
  const user = useSelector(selectUser)
  const enterprise = useSelector(selectCurrentEnterprise)
  const enterpriseLoaded = useSelector(selectEnterpriseLoaded)
  const members = useSelector(selectMembers)
  const identifiedRef = useRef<{
    userId: string
    teamSize: number
    enterpriseId: string | undefined
    role: string | undefined
  } | null>(null)

  useEffect(() => {
    // Wait for enterprise to settle so identify/group fires once with a
    // complete tenant id — otherwise the first identify lands with
    // `enterprise_id: undefined`, which conflates identities in analytics.
    if (!user.userId || !identify || !enterpriseLoaded) {
      return
    }

    // Check if we've already identified this user with the same attributes
    if (
      identifiedRef.current?.userId === user.userId &&
      identifiedRef.current?.teamSize === members.length &&
      identifiedRef.current?.enterpriseId === enterprise?.id &&
      identifiedRef.current?.role === enterprise?.userRole
    ) {
      return
    }

    try {
      identify(user.userId, {
        name: user.name,
        email: user.email,
        enterprise_id: enterprise?.id,
        role: enterprise?.userRole,
        team_size: members.length || undefined,
      })

      if (group && enterprise?.id) {
        group('enterprise', enterprise.id, {
          name: enterprise.name,
          domain: enterprise.domain,
          team_size: members.length,
        })
      }

      identifiedRef.current = {
        userId: user.userId,
        teamSize: members.length,
        enterpriseId: enterprise?.id,
        role: enterprise?.userRole,
      }
    } catch (error) {
      // PostHog can throw when blocked by ad blockers — never crash the app for analytics
      console.warn('[AnalyticsProvider] PostHog call failed:', error)
    }
  }, [user.userId, user.name, user.email, identify, group, enterprise, enterpriseLoaded, members.length])

  return children
}
